import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { FeaturedCarousel } from "../components/FeaturedCarousel";
import { PriceMatch } from "../components/PriceMatch";
import { CustomJewelry } from "../components/CustomJewelry";
import { Testimonials } from "../components/Testimonials";

const HERO_IMG = "/brand/hero.jpg";

const CATEGORY_TILES = [
  { slug: "rings", label: "Wedding Rings" },
  { slug: "ring-bands", label: "Ring Bands" },
  { slug: "bracelets", label: "Bracelets" },
  { slug: "necklaces", label: "Necklaces" },
  { slug: "earrings", label: "Earrings" },
  { slug: "", label: "Shop All" },
];

export default function Home() {
  const [heroVideo, setHeroVideo] = useState("");
  const [heroVideos, setHeroVideos] = useState([]);
  // Double-buffer: two slots that we swap between for a seamless crossfade
  const [activeSlot, setActiveSlot] = useState(0);
  const [slot0Idx, setSlot0Idx] = useState(0);
  const [slot1Idx, setSlot1Idx] = useState(1);
  const [catImages, setCatImages] = useState({});
  const [siteSettings, setSiteSettings] = useState(null);
  const videoARef = useRef(null);
  const videoBRef = useRef(null);

  useEffect(() => {
    api.get("/site/settings").then(({ data }) => {
      setSiteSettings(data);
      if (data?.hero_video_url) setHeroVideo(data.hero_video_url);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/hero-videos").then(({ data }) => {
      const list = data?.videos || [];
      if (list.length > 0) setHeroVideos(list);
    }).catch(() => {});
  }, []);

  // Preload the inactive slot with the "next" video so switching is instant.
  useEffect(() => {
    if (heroVideos.length < 2) return;
    if (activeSlot === 0) {
      setSlot1Idx((slot0Idx + 1) % heroVideos.length);
    } else {
      setSlot0Idx((slot1Idx + 1) % heroVideos.length);
    }
    // start the currently-active video
    const ref = activeSlot === 0 ? videoARef : videoBRef;
    ref.current?.play().catch(() => {});
  }, [activeSlot, heroVideos.length]);

  // When the active video ends, crossfade to the OTHER slot (which was preloading).
  const advanceHero = () => {
    if (heroVideos.length < 2) {
      const ref = activeSlot === 0 ? videoARef : videoBRef;
      ref.current?.play().catch(() => {});
      return;
    }
    setActiveSlot((s) => (s === 0 ? 1 : 0));
  };

  useEffect(() => {
    // Pull one representative photo per category from our own catalog.
    // Admin can pin a specific ring/product for a category via site settings.
    api.get("/products", { params: { limit: 200 } }).then(({ data }) => {
      const map = {};
      // Admin override map: {rings: <productId>, bracelets: <productId>, ...}
      const overrides = siteSettings?.category_hero_overrides || {};
      // Blacklist ids. allow admin to hide unwanted first picks
      const blacklist = new Set(siteSettings?.category_hero_blacklist || []);
      const pickForCategory = (slug) => {
        const pool = data.filter((p) => p.category === slug && !blacklist.has(p.id));
        if (pool.length === 0) return null;
        // 1) Admin explicit override wins
        if (overrides[slug]) {
          const p = pool.find((x) => x.id === overrides[slug]);
          if (p) return p;
        }
        if (slug === "rings") {
          // Prefer a classic round solitaire; skip the one previously showing as split-band
          const round = pool.find((p) => p.shape === "Round");
          const featured = pool.find((p) => p.featured);
          return round || featured || pool[0];
        }
        return pool.find((p) => p.featured) || pool[0];
      };
      ["rings", "ring-bands", "bracelets", "necklaces", "earrings"].forEach((slug) => {
        const chosen = pickForCategory(slug);
        if (chosen) {
          const variant = chosen.variants?.[0];
          const img = variant?.images?.[0] || chosen.image;
          if (img) map[slug] = imgUrl(img);
        }
      });
      if (map.rings) map[""] = map.rings;
      setCatImages(map);
    }).catch(() => {});
  }, [siteSettings]);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO. full-width motion */}
      <section
        data-testid="hero-section"
        className="relative w-full h-[100vh] min-h-[640px] max-h-[820px] overflow-hidden bg-black text-white"
      >
        <div className="absolute inset-0">
          {heroVideos.length > 0 ? (
            <>
              {[{ idx: slot0Idx, ref: videoARef, slot: 0 }, { idx: slot1Idx, ref: videoBRef, slot: 1 }].map(({ idx, ref, slot }) => {
                const v = heroVideos[idx];
                if (!v) return null;
                const isActive = activeSlot === slot;
                return (
                  <video
                    key={`slot-${slot}-${v.name}`}
                    ref={ref}
                    className="absolute inset-0 w-full h-full object-cover object-center scale-[1.15] md:scale-[1.75] origin-center transition-opacity duration-700 ease-out"
                    autoPlay={isActive}
                    muted
                    playsInline
                    preload="auto"
                    onEnded={isActive ? advanceHero : undefined}
                    onCanPlay={() => { if (isActive) ref.current?.play().catch(() => {}); }}
                    data-testid={isActive ? "hero-video" : `hero-video-${slot}`}
                    poster={HERO_IMG}
                    style={{ opacity: isActive ? 1 : 0, zIndex: isActive ? 2 : 1 }}
                  >
                    {v.webm && <source src={v.webm} type="video/webm" />}
                    {v.mp4 && <source src={v.mp4} type="video/mp4" />}
                  </video>
                );
              })}
            </>
          ) : heroVideo ? (
            <video
              className="w-full h-full object-cover"
              src={heroVideo}
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-video"
            />
          ) : (
            <img
              src={HERO_IMG}
              alt="Dreamonds solitaire"
              className="w-full h-full object-cover animate-ken-burns"
            />
          )}
          <div className="absolute inset-0 bg-black/35" style={{ zIndex: 3 }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/10 to-transparent" style={{ zIndex: 3 }} />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 h-full flex items-end pb-14 md:pb-20">
          <div className="max-w-2xl [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="label-track text-[#C8A165] mb-6"
            >
              The New Collection · 2026
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="font-heading font-bold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-white"
            >
              Dreamin' <br /> in <span className="italic text-[#D4B47A]">diamonds.</span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/shop"
                data-testid="hero-shop-button"
                className="bg-white text-[#0A0A0A] px-9 py-4 label-track transition-all duration-300 hover:bg-[#C8A165] hover:text-white hover:shadow-[0_10px_30px_-10px_rgba(200,161,101,0.6)]"
              >
                Shop the Collection
              </Link>
              <a
                href="#custom-jewelry-section"
                data-testid="hero-consultation-button"
                className="text-white px-9 py-4 label-track shadow-[inset_0_0_0_1px_rgba(255,255,255,0.55)] transition-all duration-300 hover:bg-white hover:text-[#0A0A0A] inline-flex items-center gap-2"
              >
                <PlayCircle size={16} strokeWidth={1.5} /> Book Consultation
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUE STRIP now pinned inside the sticky header. see Navbar.jsx */}

      {/* FEATURED CAROUSEL */}
      <FeaturedCarousel />

      {/* SHOP BY COLLECTION */}
      <section data-testid="shop-by-collection" className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-10 md:pb-16 pt-4 md:pt-6">
          <div className="flex items-end justify-between mb-4 md:mb-6">
            <div>
              <p className="label-track text-[#C8A165] mb-3">Explore</p>
              <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#0A0A0A]">
                Shop by Collection
              </h2>
            </div>
            <Link
              to="/shop"
              className="hidden sm:inline-flex items-center gap-2 label-track text-[#525252] hover:text-[#C8A165] transition-colors"
            >
              All Pieces <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {CATEGORY_TILES.map((c, i) => {
              const src = catImages[c.slug];
              return (
                <motion.div
                  key={c.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
                >
                  <Link
                    to={c.slug ? `/shop/${c.slug}` : "/shop"}
                    data-testid={`grid-cat-${c.slug || "all"}`}
                    className="group relative block aspect-square md:aspect-[5/4] overflow-hidden bg-[#F5F2EC]"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={c.label}
                        className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#F5F2EC] to-[#E7DFD2]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 inset-x-0 p-3 md:p-5 flex items-end justify-between">
                      <h3 className="font-heading text-lg md:text-2xl text-white leading-tight">
                        {c.label}
                      </h3>
                      <span className="label-track text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 hidden md:inline-flex items-center gap-2">
                        Shop Now <ArrowRight size={13} />
                      </span>
                    </div>
                    <span className="absolute top-3 left-3 md:top-5 md:left-5 label-track text-white/80 text-[9px]">
                      0{i + 1}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICE MATCH */}
      <PriceMatch />

      {/* CUSTOM JEWELRY */}
      <div id="custom-jewelry-section">
        <CustomJewelry />
      </div>

      {/* TESTIMONIALS. admin-controlled toggle */}
      {siteSettings?.show_testimonials && <Testimonials />}
    </div>
  );
}
